import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { customerLocationAssociationsService } from "../../../services/customers/customerLocationAssociations.service";
import { getUserFacingErrorMessage, showToastError, showToastSuccess } from "../../../utils";

export type EditCustomerLocationLabelRow = {
    id: string;
    locationName: string;
    label?: string | null;
};

export interface EditCustomerLocationLabelModalProps {
    show: boolean;
    siteId: string;
    customerId: string;
    association: EditCustomerLocationLabelRow | null;
    onClose: () => void;
    onSaved: () => void;
}

const EditCustomerLocationLabelModal = ({
    show,
    siteId,
    customerId,
    association,
    onClose,
    onSaved,
}: EditCustomerLocationLabelModalProps) => {
    const [label, setLabel] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!show || !association) {
            setLabel("");
            return;
        }
        setLabel(association.label ?? "");
    }, [show, association]);

    const handleSave = () => {
        if (!association) return;
        setSaving(true);
        customerLocationAssociationsService
            .updateAssociation(siteId, customerId, {
                id: association.id,
                label: label.trim(),
            })
            .then(() => {
                showToastSuccess("Label updated.");
                onSaved();
                onClose();
            })
            .catch((e: unknown) => {
                showToastError(getUserFacingErrorMessage(e, "Could not update label."));
            })
            .finally(() => setSaving(false));
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Edit Label</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {association && (
                    <p className="text-muted mb-3">
                        Location: <strong>{association.locationName}</strong>
                    </p>
                )}
                <Form.Group>
                    <Form.Label>Label</Form.Label>
                    <Form.Control
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="Optional"
                        autoFocus
                        disabled={saving}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={saving || !association}>
                    {saving ? "Updating…" : "Update"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export { EditCustomerLocationLabelModal };
